import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    MessageSquare,
    Pencil,
    Trash2,
    Check,
    X,
    ChevronDown,
    ChevronUp,
} from "lucide-react";
import type { TaskComment } from "@/types";

interface TaskCommentsProps {
    comments: TaskComment[];
    onEdit?: (commentId: string, newText: string) => void;
    onDelete?: (commentId: string) => void;
    maxVisible?: number;
    compact?: boolean;
}

export function TaskComments({
    comments,
    onEdit,
    onDelete,
    maxVisible = 2,
    compact = false,
}: TaskCommentsProps) {
    const [expanded, setExpanded] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editText, setEditText] = useState("");

    if (!comments || comments.length === 0) {
        return null;
    }

    const sortedComments = [...comments].sort(
        (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

    const visibleComments = expanded
        ? sortedComments
        : sortedComments.slice(0, maxVisible);
    const hiddenCount = sortedComments.length - maxVisible;

    const formatTimestamp = (timestamp: Date) => {
        const date = new Date(timestamp);
        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();

        if (isToday) {
            return date.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
            });
        }
        return date.toLocaleDateString([], {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const startEditing = (comment: TaskComment) => {
        setEditingId(comment.id);
        setEditText(comment.text);
    };

    const cancelEditing = () => {
        setEditingId(null);
        setEditText("");
    };

    const saveEdit = () => {
        if (editingId && editText.trim() && onEdit) {
            onEdit(editingId, editText.trim());
            setEditingId(null);
            setEditText("");
        }
    };

    const canEdit = !!onEdit;
    const canDelete = !!onDelete;

    if (compact) {
        return (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <MessageSquare className="w-3 h-3" />
                <span>{comments.length}</span>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <MessageSquare className="w-3.5 h-3.5" />
                <span className="font-medium">
                    {comments.length} comment{comments.length !== 1 ? "s" : ""}
                </span>
            </div>

            <div className="space-y-2 pl-1">
                {visibleComments.map((comment) => (
                    <div
                        key={comment.id}
                        className="group bg-slate-50 dark:bg-slate-800/50 rounded-lg p-2.5 text-sm"
                    >
                        {editingId === comment.id ? (
                            <div className="space-y-2">
                                <Input
                                    value={editText}
                                    onChange={(e) =>
                                        setEditText(e.target.value)
                                    }
                                    className="h-8 text-sm"
                                    autoFocus
                                />
                                <div className="flex gap-1.5">
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-7 px-2"
                                        onClick={cancelEditing}
                                    >
                                        <X className="w-3 h-3 mr-1" />
                                        Cancel
                                    </Button>
                                    <Button
                                        size="sm"
                                        className="h-7 px-2"
                                        onClick={saveEdit}
                                        disabled={!editText.trim()}
                                    >
                                        <Check className="w-3 h-3 mr-1" />
                                        Save
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-start justify-between gap-2">
                                    <p className="text-foreground flex-1">
                                        {comment.text}
                                    </p>
                                    {(canEdit || canDelete) && (
                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            {canEdit && (
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    className="h-6 w-6"
                                                    onClick={() =>
                                                        startEditing(comment)
                                                    }
                                                >
                                                    <Pencil className="w-3 h-3" />
                                                </Button>
                                            )}
                                            {canDelete && (
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    className="h-6 w-6 text-red-500 hover:text-red-600"
                                                    onClick={() =>
                                                        onDelete!(comment.id)
                                                    }
                                                >
                                                    <Trash2 className="w-3 h-3" />
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                    {formatTimestamp(comment.timestamp)}
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>

            {hiddenCount > 0 && (
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs w-full"
                    onClick={() => setExpanded(!expanded)}
                >
                    {expanded ? (
                        <>
                            <ChevronUp className="w-3 h-3 mr-1" />
                            Show less
                        </>
                    ) : (
                        <>
                            <ChevronDown className="w-3 h-3 mr-1" />
                            Show {hiddenCount} more
                        </>
                    )}
                </Button>
            )}
        </div>
    );
}
